import { ActionContext, ActionTree } from 'vuex'
import { IProductExtended, IRootState, IShoppingCartState } from '@/@types'
import store from '@/store'
import CONSTANTS from '@/constants'
import { mixpanelEvent } from '@/helpers/mixins'

const {
  BLACK_FRIDAY_PRODUCT_BUNDLES: { INE_BLACK_FRIDAY_BUNDLE_ONE, INE_BLACK_FRIDAY_BUNDLE_TWO },
} = CONSTANTS

const actions: ActionTree<IShoppingCartState, IRootState> = {
  addProductToShoppingCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    const newShoppingCartState = context.state.shoppingCart
    newShoppingCartState.push(product)
    context.commit('SHOPPING_CART_UPDATED', newShoppingCartState)
    context.dispatch('setSelectedPlan', context.getters.selectedPlan)
    context.commit('SET_SAVED_AT', new Date())
    mixpanelEvent.methods.gaAddToCartEvent(product, context)
  },
  removeProductFromShoppingCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    const cartMerchantIds = context.state.shoppingCart.map(item => item.merchant_id)
    context.commit('SHOPPING_CART_REMOVED_ITEM_UPDATED', product)
    context.dispatch('setSelectedPlan', context.getters.selectedPlan)
    if (
      product.merchant_id !== CONSTANTS.PLANS.SKILL_DIVE_ANNUAL &&
      cartMerchantIds.includes(product.merchant_id)
    ) {
      mixpanelEvent.methods.gaRemoveFromCartEvent(product, context)
    }

    const couponStr = store.getters['coupons/getCoupon']
    store.dispatch('coupons/updateCouponObjStack', { root: true })
    store.dispatch('priceDetails/priceDetailsUpdate', { root: true })
    store.dispatch('recurly/createRecurlyInstance', { root: true })
    store.dispatch('recurly/setRecurlyCoupon', couponStr, { root: true })
    context.commit('SET_SAVED_AT', new Date())
    context.state.shoppingCart.forEach(cartItem => {
      store.dispatch('recurly/calculateRecurlyPricing', cartItem, { root: true })
    })
  },
  setBundleProductError: (context: ActionContext<IShoppingCartState, IRootState>, payload) => {
    context.commit('BUNDLE_PRODUCT_ERROR_UPDATED', payload)
  },
  setSelectedPlan: (
    context: ActionContext<IShoppingCartState, IRootState>,
    product: IProductExtended
  ) => {
    context.commit('SHOPPING_CART_SELECTED_PLAN_UPDATED', product)
  },
  setAddOnscertificationsList: (
    context: ActionContext<IShoppingCartState, IRootState>,
    list: IProductExtended[]
  ) => {
    context.commit('ADD_ONS_CERTIFICATIONS_LIST_UPDATED', list)
  },
  getVoucherAvailability: (
    context: ActionContext<IShoppingCartState, IRootState>,
    certification: IProductExtended
  ) => {
    const restrictStandAloneCerts =
      context.rootGetters['featureFlags/isRestrictStandAloneCertsEnabled']
    if (!restrictStandAloneCerts) return true

    const isBusinessUser = store.getters['business/isBusinessUser']
    const isBusinessPlanExpired = store.getters['business/isBusinessPlanExpired']
    const userHasSubscription = store.getters['subscriptions/getUserHasSubscription']
    const cartHasPremiumSubscription = store.getters['shoppingCart/cartHasPremiumSubscription']
    const cartHasFundamentalsAnnualSubscription =
      store.getters['shoppingCart/cartHasFundamentalsAnnualSubscription']
    const cartHasFundamentalsMonthlySubscription =
      store.getters['shoppingCart/cartHasFundamentalsMonthlySubscription']
    const hasEligibleBlackFridayBundle = context.state.shoppingCart.some(item => {
      const identifier = item.slug || item.merchant_id
      return (
        identifier === INE_BLACK_FRIDAY_BUNDLE_ONE || identifier === INE_BLACK_FRIDAY_BUNDLE_TWO
      )
    })
    if (hasEligibleBlackFridayBundle) {
      return true
    }

    // Extract voucher IDs from constants and define the conditional certifications list
    const {
      VOUCHERS: { EJPT, EDA, EMAPT, EWPT, EWPTX, ECPPT, ECPPT3, ECTHP, ECIR, EJDS },
    } = CONSTANTS
    const conditionalPurchasableCertifications = [
      EJPT,
      EDA,
      EMAPT,
      EWPT,
      EWPTX,
      ECPPT,
      ECPPT3,
      ECTHP,
      ECIR,
      EJDS,
    ]

    // If the user does not have a subscription but has a subscription in their cart,
    // they are allowed access to the voucher.
    if (
      !userHasSubscription &&
      (cartHasPremiumSubscription ||
        cartHasFundamentalsAnnualSubscription ||
        cartHasFundamentalsMonthlySubscription)
    ) {
      return true
    }

    // Determine if the current certification is one that has additional conditions.
    const isConditionalCertification = conditionalPurchasableCertifications.includes(
      certification.merchant_id
    )

    // For conditional certifications, check if the user either has a subscription
    // or qualifies as a business user with a valid (non-expired) plan.
    if (isConditionalCertification) {
      return userHasSubscription || (isBusinessUser && !isBusinessPlanExpired)
    }

    // For all other cases, the voucher is available.
    return true
  },

  addAddonsToPlanInCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    { plan, addOn }: { plan: IProductExtended; addOn: IProductExtended }
  ) => {
    context.commit('ADD_ADDONS_TO_PLAN_IN_CART', { plan, addOn })
    context.commit('SET_SAVED_AT', new Date())
  },
  removeAddonsFromPlanInCart: (
    context: ActionContext<IShoppingCartState, IRootState>,
    { plan, addOn }: { plan: IProductExtended; addOn: IProductExtended }
  ) => {
    context.commit('REMOVE_ADDONS_TO_PLAN_IN_CART', { plan, addOn })
    context.commit('SET_SAVED_AT', new Date())
  },
  setselectedAddOnsByUser: (
    context: ActionContext<IShoppingCartState, IRootState>,
    selectedAddOnsByUser: boolean
  ) => {
    context.commit('SET_SELECTED_ADDONS_BY_USER', selectedAddOnsByUser)
  },
  filterAndUpdateShoppingCartWithExistingSubs: async (
    context: ActionContext<IShoppingCartState, IRootState>
  ) => {
    const currentShopping = context.getters.getShoppingCart
    const subscriptions = context.rootGetters['subscriptions/getUsersCurrentSubscriptions']
    const existingMerchantIds = subscriptions.map((sub: any) => sub.plan_code)
    const isBusinessUser = context.rootGetters['business/isBusinessUser']

    // remove existing ids
    existingMerchantIds.forEach((merchantId: string) => {
      const itemToRemove = currentShopping.find(
        (item: IProductExtended) => item.merchant_id === merchantId
      )
      if (itemToRemove) {
        context.dispatch('removeProductFromShoppingCart', itemToRemove)
      }
    })
    // remove items with product_type plan if user has subscription or is b2b
    if (existingMerchantIds.length > 0 || isBusinessUser) {
      const itemsToRemove = currentShopping.filter(
        (item: IProductExtended) => item.product_type === CONSTANTS.PRODUCT_TYPES.PLAN
      )
      itemsToRemove.forEach((item: IProductExtended) => {
        context.dispatch('removeProductFromShoppingCart', item)
      })
    }
    await context.dispatch('priceDetails/priceDetailsUpdate', {}, { root: true })
    context.dispatch('updatePricesWithExistingShoppingCart')
  },
  // Keep it short: refresh cart items with latest products
  updatePricesWithExistingShoppingCart: async (
    context: ActionContext<IShoppingCartState, IRootState>
  ) => {
    const cart = [...context.state.shoppingCart]
    const available = [
      ...context.rootGetters['products/availablePlans'],
      ...context.rootGetters['products/availableCertifications'],
      ...context.rootGetters['products/availableProductBundles'],
      ...context.rootGetters['products/availableLiveVirtualTrainings'],
      ...context.rootState.products.extensions,
    ] as IProductExtended[]

    const byId = available.reduce((m, p) => {
      if (p?.merchant_id) m.set(p.merchant_id, p as IProductExtended)
      return m
    }, new Map<string, IProductExtended>())

    const ops = cart
      .map(item => {
        const updated = byId.get(item?.merchant_id || '')
        if (!updated) return null
        const toAdd = item?.addOnsOnCart?.length
          ? ({ ...updated, addOnsOnCart: item.addOnsOnCart } as IProductExtended)
          : updated
        return context
          .dispatch('removeProductFromShoppingCart', item)
          .then(() => context.dispatch('addProductToShoppingCart', toAdd))
      })
      .filter(Boolean) as Array<Promise<void>>

    await Promise.all(ops)
    await context.dispatch('priceDetails/priceDetailsUpdate', {}, { root: true })
  },
}

export default actions
